import {
  DefaultError,
  matchMutation,
  Mutation,
  MutationCache,
  MutationFilters,
  notifyManager,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import React from "react";

export const getLastMutationState = (
  filters: MutationFilters,
  queryClient: QueryClient,
) => {
  return queryClient
    .getMutationCache()
    .getAll()
    .findLast((m) => matchMutation(filters, m));
};

const getLastMutation = (
  mutationCache: MutationCache,
  filters: MutationFilters,
) =>
  mutationCache.getAll().findLast((m) => matchMutation(filters, m)) as
    | Mutation
    | undefined;

// https://github.com/TanStack/query/blob/main/packages/react-query/src/useMutationState.ts
export const useLastMutationState = (
  filters: MutationFilters,
  queryClient?: QueryClient,
) => {
  const mutationCache = useQueryClient(queryClient).getMutationCache();
  const filtersRef = React.useRef(filters);
  const result = React.useRef<Mutation | null | undefined>(null);
  if (!result.current) {
    result.current = getLastMutation(mutationCache, filters);
  }

  React.useEffect(() => {
    filtersRef.current = filters;
  });

  return React.useSyncExternalStore(
    React.useCallback(
      (onStoreChange) =>
        mutationCache.subscribe(() => {
          const nextResult = getLastMutation(mutationCache, filtersRef.current);
          if (result.current !== nextResult) {
            result.current = nextResult;
            notifyManager.schedule(onStoreChange);
          }
        }),
      [mutationCache],
    ),
    () => result.current,
    () => result.current,
  )!;
};

export const useMutationWithCache = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  _filters?: MutationFilters,
  queryClient?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const _mutation = useMutation(options);
  const client = useQueryClient(queryClient);
  const filters: MutationFilters = {
    mutationKey: options.mutationKey,
    ..._filters,
  };
  const mutation = {
    ..._mutation,
    reset: () => {
      const cache = client.getMutationCache();
      cache
        .getAll()
        .filter((m) => matchMutation(filters, m))
        .map((m) => cache.remove(m));
      _mutation.reset();
    },
  } as UseMutationResult<TData, TError, TVariables, TContext>;
  if (!mutation?.submittedAt) {
    const cache = getLastMutationState(filters, client);
    if (cache) {
      return {
        ...{ isFromCache: true, filters },
        ...mutation,
        ...cache.state,
      } as UseMutationResult<TData, TError, TVariables, TContext>;
    }
  }
  return mutation;
};

export const useLatestMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  queryClient?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const _mutation = useMutation(options);
  const client = useQueryClient(queryClient);
  const filters = { mutationKey: options.mutationKey };
  const mutation = {
    ..._mutation,
    reset: () => {
      const cache = client.getMutationCache();
      cache
        .getAll()
        .filter((m) => matchMutation(filters, m))
        .map((m) => cache.remove(m));
      _mutation.reset();
    },
  } as UseMutationResult<TData, TError, TVariables, TContext>;
  const cache = useLastMutationState(filters, client);
  // if (!mutation?.submittedAt && cache) {
  return {
    ...mutation,
    ...cache?.state,
  } as UseMutationResult<TData, TError, TVariables, TContext>;
  // }
  // return mutation;
};

export const querySelector = <TData, TError, TDataResult>(
  query: UseQueryResult<TData, TError>,
  selector: (data?: TData) => TDataResult,
) => {
  return { ...query, data: selector(query.data) };
};

export const mutationSelector = <
  TData,
  TError,
  TVariables,
  TContext,
  TDataResult,
>(
  mutation: UseMutationResult<TData, TError, TVariables, TContext>,
  selector: (data?: TData) => TDataResult,
) => {
  return { ...mutation, data: selector(mutation.data) };
};
